import * as React from 'react';

import { Mutation, Query } from 'react-apollo';
import { ApolloError } from 'apollo-client';

import { AdminOrgApprovalSettingsQuery as AdminOrgApprovalSettingsQueryType } from '../queries/__generated__/AdminOrgApprovalSettingsQuery';
import {
  setAdminOrgApprovalSettingsMutation,
  setAdminOrgApprovalSettingsMutationVariables,
} from '../mutations/__generated__/setAdminOrgApprovalSettingsMutation';
import { SetOrgApprovalSettingsInput } from 'globalTypes';

import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ErrorReporter from 'app/components/ErrorReporter';
import ToastActions from 'actions/ToastActions';

import ADMIN_ORG_APPROVAL_SETTINGS_QUERY from '../queries/AdminOrgApprovalSettingsQuery.gql';
import SET_ADMIN_ORG_APPROVAL_SETTINGS_MUTATION from '../mutations/setAdminOrgApprovalSettingsMutation.gql';

type Props = {
  organizationUUID: string;
  children: (
    props: {
      approvalSettings: any;
      onSave: (arg0: { variables: { input: SetOrgApprovalSettingsInput } }) => void;
    }
  ) => React.ReactNode;
};

export default function AdminOrgApprovalSettingsContainer(props: Props) {
  const queryVariables = { orgUuid: props.organizationUUID };
  return (
    <Mutation<setAdminOrgApprovalSettingsMutation, setAdminOrgApprovalSettingsMutationVariables>
      mutation={SET_ADMIN_ORG_APPROVAL_SETTINGS_MUTATION}
      onCompleted={() => ToastActions.showSuccessToast('Saved')}
      onError={(error: ApolloError) => ToastActions.showErrorToast(error.message)}
      refetchQueries={[{ query: ADMIN_ORG_APPROVAL_SETTINGS_QUERY, variables: queryVariables }]}>
      {(updateOrgApprovalSettings: ((input: any) => void)) => (
        <Query<AdminOrgApprovalSettingsQueryType>
          query={ADMIN_ORG_APPROVAL_SETTINGS_QUERY}
          variables={queryVariables}
          fetchPolicy="network-only">
          {({ loading, error, data }) => {
            if (loading) {
              return <CenteredLoadingIndicator />;
            }
            const organization = data && data.organizations && data.organizations[0];
            if (!organization || error) {
              return (
                <ErrorReporter
                  error={error ?? 'AdminOrgApprovalSettingsContainer - could not load data'}
                />
              );
            }

            const { approvalSettings } = organization;

            if (!approvalSettings) {
              return (
                <ErrorReporter error="AdminOrgApprovalSettingsContainer - missing approval settings" />
              );
            }

            return props.children({
              approvalSettings: approvalSettings,
              onSave: (updatedOrgSettings: {
                variables: { input: SetOrgApprovalSettingsInput };
              }): void => {
                updateOrgApprovalSettings(updatedOrgSettings);
              },
            });
          }}
        </Query>
      )}
    </Mutation>
  );
}
